import {
  firstVisible,
  whenElementVisible,
  escapeHtml,
  linkedHtml,
  templateHtml,
  dropStaleHighlights,
  collapsedTarget,
  revealPanelFor,
  openPanelIds,
  restorePanels,
} from './helpers';
import { extensionPresent } from '../controllers/helpers/extension_presence';

// Wide enough for the screencap a step carries — the same box the other tours' captures fill.
const MEDIA_POPOVER = 'unscreen-tour-popover unscreen-tour-popover--capture';

// Points at what the dashboard already shows — the menu, the day's cards, the week's sections, the "?" that
// offers a tutorial elsewhere. Copy, order and selectors come from Tours::DashboardIntro. The last step is
// inside a collapsed sidebar menu, so that one panel is opened on the way past and put back on the way out.
// Mounted manually by home/dashboard, so it runs only when asked for: the page title's "?", or the
// "Learn The Basics" headline card.

export default {
  mount({ start, payload }) {
    const steps = payload.steps || [];
    if (!steps.length) return undefined;

    let openOnArrival = new Set();

    // A step's target may be a sidebar link inside a shut panel, which has no layout box until the step opens
    // it.
    const resolve = (selector) => firstVisible(selector) ?? collapsedTarget(selector);

    // Not extensionDetected: the dashboard has no install alert, so that would always read as detected.
    const describe = (step) => (step.installed_description && extensionPresent() ? step.installed_description : step.description);

    // Selectors resolve against the document, not the mounted element: the menu, the day's cards and the page
    // header all sit outside it. A step with no selector gets no element, which driver.js centres on the page.
    // A step's media comes from a <template> the page parked it in; driver.js has one description slot, so the
    // copy goes underneath it. popoverClass carries the base class too — driver.js takes a step's over the
    // config's rather than adding to it.
    const stepFor = (step) => {
      const media = step.template ? templateHtml(step.template) : null;
      const copy = linkedHtml(describe(step));

      return {
        ...(step.selector ? { element: () => resolve(step.selector) } : {}),
        popover: {
          title: escapeHtml(step.title),
          description: media ? `${media}<div class="tour-text">${copy}</div>` : copy,
          side: step.side,
          align: step.align,
          ...(media ? { popoverClass: MEDIA_POPOVER } : {}),
        },
        onHighlightStarted: (target) => {
          dropStaleHighlights(target);
          revealPanelFor(target);
        },
      };
    };

    // Filtered up front: driver.js centres a step whose element resolves null, leaving copy about a card this
    // visitor hasn't got reading like the opening one.
    const begin = () => {
      const present = steps.filter(({ selector }) => !selector || resolve(selector));
      if (!present.some(({ selector }) => selector)) return;

      openOnArrival = openPanelIds();
      start({ steps: present.map(stepFor), onDestroyed: () => restorePanels(openOnArrival) });
    };

    const anchors = steps.map(({ selector }) => selector).filter(Boolean);
    return whenElementVisible(anchors.join(', '), begin);
  },
};
